import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import useNeuroState from '../state';
import { getMessages,  useRuleEvaluator } from '../utils';

/**
 * @typedef MessageContextObject
 * @property {Object} customMessages - custom key -> any values, used to override typical message formatting
 * @property {Object} formattingValues - returns values for HTML formatting and answer literals
 * @property {function(descriptor:Object, values:Object=): any} formatIntlMessage - returns formatted message from react-intl
 * @property {function(id:any): boolean} hasMessage - return true if message key exists
 * @property {function(id:any): (string|null)} getMessage - returns a non-formatted message if found
 * @property {Object} intlMessages - key -> string values
 * @property {string} locale - current locale of the application
 * @property {function(id:any): boolean} messageHasRule - returns true if message rule exists
 * @property {function(id:any): boolean} messageRuleIsValid - returns true if message rule is valid
 * @type MessageContextObject
 */
const defaultValue = {};
const MessageContext = createContext(defaultValue);

const MessageProvider = ({ children, customMessages, htmlMessageValues }) => {
  const intl = useIntl();
  const { answers = {} } = useNeuroState(); //TODO: change to use answers
  const ruleEvaluator = useRuleEvaluator();
  const locale = intl.locale;
  const intlMessages = getMessages(locale);
  const answersMap = {};
  React.useMemo(() => {
    for (let [key, answer] of Object.entries(answers)) {
      if( answer?.value ) {
        answersMap[`answer__${key}`] = answer.value.toString();
      }
    }
  }, [answers]);
  const formattingValues = {
    ...defaultHtmlFormatValues,
    ...htmlMessageValues,
    ...answersMap
  };

  const hasMessage = (id) => !!(customMessages?.[id] || intlMessages?.[id]);
  const getMessage = (id) => customMessages?.[id] || intlMessages?.[id];
  const formatIntlMessage = (descriptor, values) =>
    intl.formatMessage(descriptor, { ...formattingValues, ...values });
  const messageHasRule = (id) => hasMessage(`${id}.rule`);
  const messageRuleIsValid = (id) => {
    const messageRule = getMessage(`${id}.rule`);
    return messageRule && ruleEvaluator.isTrue(messageRule);
  };

  /**
   * @type MessageContextObject
   */
  const contextValue = {
    customMessages,
    formattingValues,
    formatIntlMessage,
    getMessage,
    hasMessage,
    intlMessages,
    locale,
    messageHasRule,
    messageRuleIsValid,
  };
  return (
    <MessageContext.Provider value={contextValue}>
      {children}
    </MessageContext.Provider>
  );
};

/**
 * Hook function for using application messages
 * @returns MessageContextObject
 */
function useNeuroMessages() {
  return useContext(MessageContext);
}

/* eslint-disable react/display-name */
const defaultHtmlFormatValues = {
  b: (...chunks) => <b>{chunks}</b>,
  br: <br />,
  del: (...chunks) => <del>{chunks}</del>,
  div: (...chunks) => <div>{chunks}</div>,
  em: (...chunks) => <em>{chunks}</em>,
  h1: (...chunks) => <h1>{chunks}</h1>,
  h2: (...chunks) => <h2>{chunks}</h2>,
  h3: (...chunks) => <h3>{chunks}</h3>,
  h4: (...chunks) => <h4>{chunks}</h4>,
  h5: (...chunks) => <h5>{chunks}</h5>,
  href: (...chunks) => {
    const chunkString = chunks.toString();
    if ( chunkString.includes('||')) {
      const [ url, text ] = chunks.toString().split('||');
      return <a href={url.trim()} target="_blank" rel="noreferrer">{text.trim()}</a>;
    } else {
      return undefined;
    }
  },
  i: (...chunks) => <i>{chunks}</i>,
  icon: (...chunks) => <i className={chunks}/>,
  ins: (...chunks) => <ins>{chunks}</ins>,
  li: (...chunks) => <li>{chunks}</li>,
  mark: (...chunks) => <mark>{chunks}</mark>,
  small: (...chunks) => <small>{chunks}</small>,
  strong: (...chunks) => <strong>{chunks}</strong>,
  span: (...chunks) => <span>{chunks}</span>,
  sub: (...chunks) => <sub>{chunks}</sub>,
  sup: (...chunks) => <sup>{chunks}</sup>,
  ul: (...chunks) => <ul>{chunks}</ul>,
  value: '',
  //TODO: add support for custom HTML values from guideConfig
};
/* eslint-enable react/display-name */

MessageProvider.defaultProps = {
  children: null,
  customMessages: null,
  htmlMessageValues: null
};
MessageProvider.propTypes = {
  children: PropTypes.node,
  customMessages: PropTypes.shape({}),
  htmlMessageValues: PropTypes.object
};
export { useNeuroMessages, MessageProvider, defaultHtmlFormatValues };
